import { Box, Button, classSet, Pagination, SearchInput, SearchableList, useFilteredPagination, BackgroundKeyword } from '@platform/ui-library';
import { FC, useState } from 'react';
import { useIntentApplications } from '../../hooks';
import { CommonIntent, DesignIntent } from '../../types';
import { convertToSentence } from '../../utilities/functions/string/convert-to-sentence';
import { Table } from '../table';
import styles from './IntentDetails.module.scss';

interface LinkToLibraryProps {
  commonIntents: CommonIntent[];
  designIntent: DesignIntent;
  onClickCancel: () => void;
  onClickLinkIntent: (
    commonIntent: CommonIntent | null
  ) => void;
  onClickViewIntent: (c: CommonIntent | undefined) => void;
  selectedCommonIntent: CommonIntent | undefined;
}

export const LinkToLibrary: FC<LinkToLibraryProps> = ({
  commonIntents,
  designIntent,
  onClickCancel,
  onClickLinkIntent,
  onClickViewIntent,
  selectedCommonIntent
}) => {
  const { intentId } = designIntent.props.config.commonIntent ?? {};
  const isAlreadyLinked = selectedCommonIntent?.commonId === intentId;
  const [commonIntentTableSearch, setCommonIntentTableSearch] = useState('');
  const intentName = selectedCommonIntent?.name ?? '';
  const applications = useIntentApplications(intentName);

  const tableData = commonIntents.map((commonIntent, idx) => {
    const { commonId, name, localeData, countUsage } = commonIntent;
    return {
      id: commonId,
      name,
      utterances: { raw: localeData['en-US']?.phrases.length ?? idx, content: <p style={{ textAlign: 'center' }}>{localeData['en-US']?.phrases.length}</p> },
      'DsId count': { raw: countUsage ?? idx, content: <p style={{ textAlign: 'center' }}>{countUsage}</p> },
      _: {
        raw: idx,
        content: (
          <Button
            key={commonId}
            type="button"
            theme="outlineHighlight"
            onClick={() => onClickViewIntent(commonIntent)}
          >
            View
          </Button>
        )
      }
    };
  });

  const {
    filteredItemsCount,
    hasMultiplePages,
    items: paginatedTableData,
    onSort,
    page,
    pageSize,
    setPage,
    setSearchString,
    sortAsc,
    sortColumn
  } = useFilteredPagination(tableData, ['name'], 1, 2);

  const subtitle = (selectedCommonIntent) ? (
    'Import our recommended intent rather than creating a new one.'
  ) : (
    'You can import these intents to add functionality to your application with built-in utterances.'
  );

  const header = (
    <div className={classSet([styles.header, styles.withSubtitle])}>
      <h2 className={classSet([styles.title, styles.withIcon])}>
        Link Library Intent
        <Button icon="cross" theme={['outline', 'small', 'round']} onClick={onClickCancel} />
      </h2>
      <p className={styles.subtitle}>{subtitle}</p>
    </div>
  );

  if (selectedCommonIntent) {
    return (
      <Box className="flex-column slide-in" theme="noPadding" background={BackgroundKeyword.Nearest}>
        {header}
        <div className="box content flex-grow">
          <div className={styles.intentView}>
            <div>
              <p>Intent Name: <strong>{selectedCommonIntent.name}</strong></p>
              {!!applications?.length && (
                <p>
                  Used by:
                  <strong>
                    {` ${applications.join(', ')}`}
                  </strong>
                </p>
              )}
              {!!selectedCommonIntent?.description &&
                  <p>{convertToSentence(selectedCommonIntent.description)}</p>
              }
              <SearchableList
                hasCount
                label="Utterances"
                disabled
                items={selectedCommonIntent.localeData['en-US']?.phrases.map((phrase, i) => ({ id: 'utt'+i, value: phrase } )) ?? []}
              />
            </div>
            <div>
              <p>Regular Expression</p>
              <p>{selectedCommonIntent.localeData['en-US']?.regularExpression}</p>
              <hr />
              <SearchableList
                hasCount={false}
                label="Intent Hierarchy (Parents)"
                disabled
                items={selectedCommonIntent.parents.map((intent) => ({ id: intent.commonId, value: intent.name })) ?? []}
              />
              <hr />
              <SearchableList
                hasCount={false}
                label="Intent Hierarchy (Children)"
                disabled
                items={selectedCommonIntent.children.map((intent) => ({ id: intent.commonId, value: intent.name })) ?? []}
              />
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <Button type="button" theme="outline" onClick={() => onClickViewIntent(undefined)}>
            Back
          </Button>
          <div className={styles.buttonGroup}>
            <Button type="button" theme="outline" onClick={onClickCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              theme="default"
              onClick={() => {
                onClickLinkIntent(
                  (
                    isAlreadyLinked
                    || !selectedCommonIntent
                  ) ? null : selectedCommonIntent
                );
              }}
            >
              {(isAlreadyLinked) ? 'Unlink' : 'Link'}
            </Button>
          </div>
        </div>
      </Box>
    );
  }

  return (
    <Box className="flex-column slide-in" theme="noPadding">
      {header}
      <div className="box content flex-grow">
        <SearchInput
          name="searchIntents"
          placeholder="Search Intents"
          onChange={(inputValue) => {
            setCommonIntentTableSearch(inputValue);
            setSearchString(inputValue);
          }}
          extraClasses={styles.input}
          value={commonIntentTableSearch}
        />
        <Table
          className={styles.table}
          data={paginatedTableData}
          sortByColumns={['name']}
          sortColumn={sortColumn}
          sortAsc={sortAsc}
          onSort={onSort}
        />
        {hasMultiplePages && (
          <Pagination
            totalItems={filteredItemsCount}
            itemsPerPage={pageSize}
            initialPage={page}
            onPageChange={setPage}
          />
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.buttonGroup}>
          <Button type="button" theme="outline" onClick={onClickCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Box>
  );
};
